import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as authApi from '@/services/authApi';
import { Button } from '@/components/ui/button';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError('Authentication failed - no session ID found in URL');
          setIsProcessing(false);
          toast.error('Authentication failed - no session ID');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for user data
        // Token is now saved in authApi.googleCallback()
        const result = await authApi.googleCallback(sessionId);
        
        toast.success(`Welcome, ${result.user.name}!`);
        
        // Redirect based on role
        if (result.user.role === 'Admin') {
          navigate('/admin/dashboard', { 
            state: { user: result.user },
            replace: true 
          });
        } else {
          navigate('/dashboard', { 
            state: { user: result.user },
            replace: true 
          });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        const errorDetail = error.response?.data?.detail || error.message || 'Google authentication failed';
        setError(errorDetail);
        setIsProcessing(false);
        toast.error(errorDetail);
      }
    };

    processAuth();
  }, [location, navigate]);

  // Show error state if auth failed
  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-[#1A1F2E] mb-2">Authentication Failed</h2>
          <p className="text-[#5A5F6E] mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/login')}
            className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state while processing
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-[#2D5F4F]/10 flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#2D5F4F]" />
        </div>
        <p className="text-[#1A1F2E] font-medium text-lg">Completing sign in...</p>
        <p className="text-[#5A5F6E] text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;